import { useLocation } from "react-router-dom";
import { useMemo } from "react";
import type { PortfolioType } from "@repo/api";

export type StrategyDetailType =
  | "delivered" // /delivered/:universe/:range/:modelId
  | "draft-only" // /draft
  | "active" // /:id
  | "draft"; // /:id/draft

export interface StrategyDetailActions {
  showEdit: boolean;
  showDelete: boolean;
  showDraft: boolean;
  showActive: boolean;
  showAddToOverview: boolean;
  showTradeOnRoboMarkets: boolean;
  showSaveAsActive: boolean;
}

export interface UseStrategyDetailTypeResult {
  type: StrategyDetailType;
  actions: StrategyDetailActions;
  isDelivered: boolean;
  isDraftOnly: boolean;
  isActive: boolean;
  isDraft: boolean;
}

export const useStrategyDetailType = (
  portfolio?: PortfolioType
): UseStrategyDetailTypeResult => {
  const location = useLocation();

  const result = useMemo(() => {
    const pathname = location.pathname;

    // Check for delivered pattern: /delivered/:universe/:range/:modelId
    if (pathname.startsWith("/delivered/")) {
      return {
        type: "delivered" as StrategyDetailType,
        actions: {
          showEdit: false,
          showDelete: false,
          showDraft: false,
          showActive: false,
          showAddToOverview: true,
          showTradeOnRoboMarkets: false,
          showSaveAsActive: false,
        },
        isDelivered: true,
        isDraftOnly: false,
        isActive: false,
        isDraft: false,
      };
    }

    // Check for draft-only pattern: /draft
    if (pathname === "/draft") {
      return {
        type: "draft-only" as StrategyDetailType,
        actions: {
          showEdit: true,
          showDelete: true,
          showDraft: false,
          showActive: false,
          showAddToOverview: true,
          showTradeOnRoboMarkets: false,
          showSaveAsActive: false,
        },
        isDelivered: false,
        isDraftOnly: true,
        isActive: false,
        isDraft: false,
      };
    }

    // Check for draft pattern: /:id/draft
    if (pathname.endsWith("/draft")) {
      return {
        type: "draft" as StrategyDetailType,
        actions: {
          showEdit: true,
          showDelete: true,
          showDraft: false,
          showActive: true,
          showAddToOverview: false,
          showTradeOnRoboMarkets: false,
          showSaveAsActive: true,
        },
        isDelivered: false,
        isDraftOnly: false,
        isActive: false,
        isDraft: true,
      };
    }

    // Default to active pattern: /:id
    const hasDraftData = Boolean(
      portfolio?.draft?.modelId && portfolio?.draft?.name
    );

    return {
      type: "active" as StrategyDetailType,
      actions: {
        showEdit: true,
        showDelete: true,
        showDraft: hasDraftData,
        showActive: false,
        showAddToOverview: false,
        showTradeOnRoboMarkets: true,
        showSaveAsActive: false,
      },
      isDelivered: false,
      isDraftOnly: false,
      isActive: true,
      isDraft: false,
    };
  }, [location.pathname, portfolio?.draft]);

  return result;
};
